import {PDFDocument} from "pdf-lib";
import {
    isActivityCarBusState,
    isActivityFlightState,
    isActivityHotelState,
    isActivityWithReturnRouteProps
} from "../utils/check/type-guards";
import {
    calculateFirstRowPosition,
    calculateHeightOfTitleAndBody,
    conditionallyAddPageToTravelComponentsPdf,
    drawActivityTotalEmissions,
    drawBodyTitleAndBody,
    drawEmissionsSummary,
    generateBodyText,
    LogoProps,
    setFooter,
    setHeader,
    setupGeneralPdfProps,
    setupPdfFonts,
    setUpPdfPageMargins,
    SPACER_TO_CO2_KG_E
} from "./utils";
import {i18n} from "i18next";
import {ACTIVITY_TYPE, AllActivityTypesArray} from "../types-enums/activity";
import fontkit from '@pdf-lib/fontkit'
import {CALCULATE_EMISSIONS_RESPONSE_STATUS_TOTAL} from "../api/model/common";
import {getBackendCalculationStatus} from "../utils/getter/emissions";
import {generateHotelGuestsCountString} from "../utils/string/hotel";
import {createFlightTripPassengersCountString} from "../utils/string/flight";
import {generateCarBusPassengerCountString} from "../utils/string/car-bus";
import {generateTranslatedActivityTypeString} from "../utils/string/common";
import {activityHasReturnOption} from "../utils/check/activities";

export const createTravelComponentsPdf = async (
    activities: AllActivityTypesArray,
    i18n: i18n,
    agencyName: string,
    agencyAddress: string,
    userName: string,
    email: string,
    completePhoneNumber: string,
    logo: LogoProps
) => {
    const {resolvedLanguage, t} = i18n
    const pdfDoc = await PDFDocument.create()
    pdfDoc.setSubject('PDF.TRAVEL_COMPONENTS.PDF_SUBJECT');
    pdfDoc.setTitle(t("PDF.TRAVEL_COMPONENTS.PDF_TITLE"));
    pdfDoc.registerFontkit(fontkit)
    let page = pdfDoc.addPage()
    const {fontBold, fontNormal} = await setupPdfFonts(pdfDoc)
    const {pageMarginLeft, pageMarginRight} = setUpPdfPageMargins()
    const {width: pageWidth, height: pageHeight} = page.getSize()
    const headerHeight = await setHeader(
        page,
        pageHeight,
        pageWidth,
        fontNormal,
        fontBold,
        pageMarginLeft,
        pageMarginRight,
        i18n,
        pdfDoc,
        userName,
        agencyName,
        agencyAddress,
        logo,
        email,
        completePhoneNumber
    )
    const {
        footerHeight,
        titleFontSize,
        marginFromBodyToTitle,
        rowGapForTravelComponentsPdf: rowGap,
        bodyFontSize,
        bodyLineHeight,
        totalHeaderHeightWithMargin,
        heightForContent
    } = setupGeneralPdfProps(headerHeight, pageHeight)
    setFooter(page, pageHeight, pageWidth, fontNormal, fontBold, footerHeight, pageMarginLeft, pageMarginRight, i18n)
    let {
        heightLeftOver,
        firstRowTitleYPosition: titleYPosition,
        firstBodyYPosition: bodyYPosition,
    } = calculateFirstRowPosition(
        pageHeight,
        totalHeaderHeightWithMargin,
        marginFromBodyToTitle,
        heightForContent
    )
    const originalTitleYPosition = titleYPosition
    let activitiesWithReturn: Set<string> = new Set()
    activities.forEach(activity => {
        if (activityHasReturnOption(activity) && activity.returnId) activitiesWithReturn.add(activity.returnId)
    })
    activities.forEach((activity, index) => {
        const {common: {totalEmissions}} = activity
        const status = getBackendCalculationStatus(totalEmissions)
        if (status !== CALCULATE_EMISSIONS_RESPONSE_STATUS_TOTAL.failed && !activitiesWithReturn.has(activity.common.id)) {
            const {
                co2Text,
                bodyText,
                textLineCount,
                totalTextLinesCount,
                totalCo2KgEOfActivity
            } = generateBodyText(
                activity,
                activities,
                i18n,
                fontNormal,
                bodyFontSize,
                pageWidth - pageMarginRight - pageMarginLeft - SPACER_TO_CO2_KG_E,
                " ",
                false,
                isActivityWithReturnRouteProps(activity) && activity.returnId !== null
            )
            const {totalHeight} = calculateHeightOfTitleAndBody(
                totalTextLinesCount,
                bodyLineHeight,
                titleFontSize,
                marginFromBodyToTitle
            )
            heightLeftOver -= totalHeight
            const result = conditionallyAddPageToTravelComponentsPdf(
                heightLeftOver,
                totalHeight,
                footerHeight,
                rowGap,
                pdfDoc,
                page,
                pageHeight,
                pageWidth,
                fontNormal,
                fontBold,
                pageMarginLeft,
                pageMarginRight,
                i18n,
                userName,
                agencyName,
                agencyAddress,
                logo,
                email,
                completePhoneNumber,
                heightForContent,
                titleYPosition,
                bodyYPosition,
                marginFromBodyToTitle,
                originalTitleYPosition,
            )
            heightLeftOver = result.heightLeftOver
            titleYPosition = result.titleYPosition
            bodyYPosition = result.bodyYPosition
            page = result.page
            let co2EBodyYPosition = bodyYPosition
            const {common: {activityType}} = activity
            let titleText: string | null = null
            // This generates the headline string for each Activity
            // 1. Car for 1 Person
            // 2. Hotel for 1 Guest
            // 3. Flight 3. One-way for 1 Person
            switch (activityType) {
                case ACTIVITY_TYPE.flight:
                    if (isActivityFlightState(activity)) {
                        const {passengerCount, returnId} = activity
                        titleText = `${index + 1}. ${generateTranslatedActivityTypeString("flight", i18n)} ${createFlightTripPassengersCountString(
                            Boolean(returnId),
                            0,
                            passengerCount,
                            false,
                            i18n
                        )}`
                    }
                    break;
                case ACTIVITY_TYPE.hotel:
                    if (isActivityHotelState(activity)) {
                        const {guestCount} = activity
                        titleText = `${index + 1}. ${generateHotelGuestsCountString(guestCount, i18n)}`
                    }
                    break;
                case ACTIVITY_TYPE.car_bus:
                    if (isActivityCarBusState(activity)) {
                        const {vehicleType, passengerCount} = activity
                        titleText = `${index + 1}. ${generateCarBusPassengerCountString(
                            vehicleType!!,
                            passengerCount,
                            i18n
                        )}`
                    }
                    break;
                default:
                    return activityType satisfies never
            }
            if (titleText !== null) {
                drawBodyTitleAndBody(
                    i18n,
                    page,
                    pageMarginLeft,
                    bodyYPosition,
                    bodyFontSize,
                    titleFontSize,
                    bodyLineHeight,
                    titleYPosition,
                    fontBold,
                    pageWidth,
                    pageMarginRight,
                    fontNormal,
                    titleText,
                    bodyText,
                    totalCo2KgEOfActivity,
                    resolvedLanguage
                )
                co2Text.forEach((text, index) => {
                    drawActivityTotalEmissions(
                        activity,
                        co2EBodyYPosition,
                        page,
                        bodyFontSize,
                        bodyLineHeight,
                        pageWidth,
                        pageMarginRight,
                        fontNormal,
                        text,
                    )
                    co2EBodyYPosition = co2EBodyYPosition - textLineCount[index] * bodyLineHeight
                })
            }
            co2EBodyYPosition = co2EBodyYPosition - textLineCount[index] * bodyLineHeight
            titleYPosition = titleYPosition - (totalHeight + rowGap)
            bodyYPosition = titleYPosition - marginFromBodyToTitle
        }
    })

    drawEmissionsSummary(
        activities,
        pageWidth,
        bodyLineHeight,
        bodyFontSize,
        titleFontSize,
        marginFromBodyToTitle,
        pdfDoc,
        heightLeftOver,
        footerHeight,
        rowGap,
        page,
        pageHeight,
        fontNormal,
        fontBold,
        pageMarginLeft,
        pageMarginRight,
        i18n,
        userName,
        agencyName,
        agencyAddress,
        logo,
        email,
        completePhoneNumber,
        heightForContent,
        titleYPosition,
        bodyYPosition,
        originalTitleYPosition,
        'perActivity'
    )


    return await pdfDoc.save()
}